/**
 * HistoryClampBanner — surfaced on any screen that displays training history
 * (Strength workouts, Combatives sessions, Endurance entries) when the
 * backend has clamped the response to the rolling 30-day free-tier window.
 *
 * Renders inline above the history list. Click → opens PaywallModal with
 * the `history_unlimited` copy. Dismissible per-session.
 */
import React, { useState } from 'react';
import { useProfile } from '../context/ProfileContext';
import { useTierState } from '../hooks/useTierState';
import { Clock, X, ArrowRight } from 'lucide-react';
import PaywallModal from './PaywallModal';

const DISMISS_KEY = 'enferno_history_clamp_dismissed_session';

const HistoryClampBanner = () => {
  const { historyClamped, historyClampDays } = useProfile();
  const { effective_tier: effectiveTier, _override: devOverride } = useTierState();
  const [paywallOpen, setPaywallOpen] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    try { return sessionStorage.getItem(DISMISS_KEY) === '1'; } catch { return false; }
  });

  // Show the banner when:
  //  (a) the server actually clamped the response (real free user), OR
  //  (b) the dev "View as Egnite" override is on (for UI verification — the
  //      list itself isn't actually clamped server-side under override, since
  //      the backend trusts the auth token, but the banner UX is what we're
  //      verifying here)
  const shouldShow = historyClamped || (devOverride && effectiveTier === 'egnite');
  if (!shouldShow || dismissed) return null;

  const dismiss = () => {
    try { sessionStorage.setItem(DISMISS_KEY, '1'); } catch { /* ignore */ }
    setDismissed(true);
  };

  const days = historyClampDays || 30;

  return (
    <>
      <div
        className="relative mb-4 rounded-lg border border-enf-accent-fg/30 bg-enf-accent/5 px-4 py-3 flex items-center gap-3"
        data-testid="history-clamp-banner"
      >
        <Clock className="w-4 h-4 text-enf-accent-fg flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-enf-text-primary">
            Showing your last {days} days{devOverride ? ' (dev preview)' : ''}
          </div>
          <p className="text-xs text-enf-text-secondary mt-0.5">
            Egnite (free) keeps a rolling window. Enferno keeps every session you&apos;ve ever logged.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setPaywallOpen(true)}
          data-testid="history-clamp-cta"
          className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 bg-enf-accent hover:bg-enf-accent-hover text-enf-text-primary rounded-md text-xs font-semibold transition-colors flex-shrink-0"
        >
          Unlock <ArrowRight className="w-3 h-3" />
        </button>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss history limit banner"
          data-testid="history-clamp-dismiss"
          className="text-enf-text-muted hover:text-enf-text-primary p-1 transition-colors flex-shrink-0"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Mobile-only CTA below the banner (the inline button is hidden on small screens) */}
      <button
        type="button"
        onClick={() => setPaywallOpen(true)}
        className="sm:hidden mb-4 w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 bg-enf-accent hover:bg-enf-accent-hover text-enf-text-primary rounded-md text-xs font-semibold transition-colors"
      >
        Unlock unlimited history <ArrowRight className="w-3 h-3" />
      </button>

      <PaywallModal open={paywallOpen} onOpenChange={setPaywallOpen} feature="history_unlimited" />
    </>
  );
};

export default HistoryClampBanner;
